import {
    OpeCollateralDTO,
    PostCollateralsAssetsDTO,
} from 'api/application/types.d'
import AssetDataSource from 'state/enums/AssetDataSource'
import AssetType from 'state/enums/AssetType'
import { parseOpeMsrp, sortByHighestMsrp } from 'utils/sortByHighestMsrp'

type OpeVehicleForm = {
    selectedVehicleId: string
    year: string | number
    make?: string
    model?: string
    asset_value?: string | number | null
    equipmentType?: string
    equipmentSubType?: string
    msrp?: string | number | null
    oem?: string | null
    sku?: string | null
}

type OpeFormVehicleValues = {
    condition: 'new'
    opeUnits: (OpeVehicleForm | undefined)[]
}

function toOpeCollateral(vehicle: OpeVehicleForm): OpeCollateralDTO {
    const [vehicleIdPart = '', selectedEquipmentType = ''] = (
        vehicle.selectedVehicleId ?? ''
    ).split(':')

    const vehicleId = Number(vehicleIdPart)
    const year = Number(vehicle.year)

    if (
        !/^\d+$/.test(vehicleIdPart) ||
        !Number.isInteger(vehicleId) ||
        vehicleId <= 0
    ) {
        throw new Error('A valid OPE catalog id is required')
    }

    if (!Number.isInteger(year) || year <= 0) {
        throw new Error('A valid OPE year is required')
    }

    const parsedMsrp = parseOpeMsrp(vehicle.msrp)
    const parsedAssetValue = parseOpeMsrp(vehicle.asset_value)
    const resolvedAssetValue = parsedAssetValue ?? parsedMsrp

    return {
        asset: {
            equipment_type: vehicle.equipmentType || selectedEquipmentType,
            equipment_sub_type: vehicle.equipmentSubType,
            condition: 'N',
            year,
            make: vehicle.make,
            model: vehicle.model,
            msrp: parsedMsrp,
            outdoor_power_equipment: vehicleId,
            sku: vehicle.sku ?? null,
            ...(vehicle.oem !== undefined ? { oem: vehicle.oem } : {}),
        },
        asset_type: AssetType.OUTDOOR_POWER_EQUIPMENT,
        asset_sales_price: resolvedAssetValue,
        asset_value: resolvedAssetValue,
        data_source: AssetDataSource.LOS,
    }
}

function isDefined(unit?: OpeVehicleForm): unit is OpeVehicleForm {
    return Boolean(unit)
}

function selectedUnits(values: OpeFormVehicleValues): OpeVehicleForm[] {
    const units = values.opeUnits.filter(isDefined)

    if (units.length > 0) {
        return sortByHighestMsrp(units, (unit) => unit.msrp)
    }

    throw new Error('At least one OPE unit is required')
}

export function createPostOpeCollateralsAssetsDTO(
    values: OpeFormVehicleValues,
): PostCollateralsAssetsDTO<OpeCollateralDTO> {
    return { collateral: selectedUnits(values).map(toOpeCollateral) }
}
